import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCompareTexts    as getCompareTexts,
  getDashboardComparePtp      as getComparePtp,
  getDashboardComparePayments as getComparePayments,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PURPLE = "#7F77DD";

// Palette cycled across campaigns so each campaign keeps a stable color
// across every chart on the page. Picked to stay distinct at 18+ series.
const SERIES_COLORS = [
  BLUE, GREEN, PINK, AMBER, PURPLE,
  "#185FA5", "#3DBE92", "#A01F50", "#D4870F", "#5B53B5",
  "#2C6FB0", "#159A6E", "#B83A66", "#9A6112", "#6A62C4",
  "#5AA0E0", "#62C9A0", "#C76A8E",
];

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

// Per-row pixel height for horizontal bar charts, plus chart chrome
// (axis + legend). Chart height scales with campaign count so bars stay
// readable whether there are 3 or 25 campaigns.
const ROW_PX        = 26;
const CHART_CHROME  = 48;
const STACK_CHROME  = 72; // stacked chart also has a legend

function chartHeight(n, chrome = CHART_CHROME) {
  return Math.max(140, n * ROW_PX + chrome);
}

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday; end = now; break;
    case "CW+Last": start = lastMonday; end = now; break;
    case "This Mo": start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtInt(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "0";
  return num.toLocaleString();
}

// Return a copy of rows sorted desc by a numeric field, so the chart's
// bars run highest (top) → lowest (bottom). Tables keep their own sort.
function sortedBy(rows, key) {
  return [...rows].sort((a, b) => safeNum(b[key]) - safeNum(a[key]));
}

// ── Chart gutter layout ─────────────────────────────────────────────
// Horizontal bars render as:  [ value ][ campaign name ][ ==== bar ==== ]
// The value and the name live in a fixed left gutter (drawn via a custom
// YAxis tick) so they never overlap the bar geometry. The name column
// width adapts to the longest campaign name in the dataset; the value
// column is fixed. The bar plot starts at VALUE_W + nameW + pad.
const VALUE_W   = 54;          // right-aligned value column
const NAME_PAD  = 10;          // gap between value and name
const PLOT_PAD  = 10;          // gap between name and bar start
const NAME_PX   = 6.2;         // approx px per char of the 11px name font
const NAME_MIN  = 60;
const NAME_MAX  = 200;

// Compute the name-column width (px) for the longest campaign label.
function nameColWidth(rows) {
  const longest = (rows || []).reduce(
    (m, r) => Math.max(m, String(r.campaign || "").length), 0);
  return Math.min(NAME_MAX, Math.max(NAME_MIN, Math.round(longest * NAME_PX + 8)));
}
function gutterWidth(nameW) {
  return VALUE_W + NAME_PAD + nameW + PLOT_PAD;
}

// Custom YAxis tick: draws the metric value (right-aligned, leftmost)
// then the campaign name (left-aligned, just right of it), both inside
// the left margin and vertically centered on the row. Fires once per
// category, so a zero-value or zero-segment row never drops its label.
//   rowsByName → { campaign: row }
//   valueKey   → which numeric field to show as the value
//   format     → "pct" | "cur" | "int"
//   nameW      → name-column width in px (for clipping long names)
function makeGutterTick(rowsByName, valueKey, format, nameW) {
  const fmt = format === "pct" ? (v => fmtPct(v))
            : format === "cur" ? fmtCurrency : fmtInt;
  return function GutterTick(props) {
    const { x, y, payload } = props;
    const name  = String(payload.value);
    const row   = rowsByName[name];
    const value = row ? safeNum(row[valueKey]) : 0;
    // x is the axis line (= gutter right edge / bar start). Lay text out
    // to the LEFT of it: name occupies [x-nameW, x], value sits before.
    const nameLeft   = x - nameW;
    const valueRight = nameLeft - NAME_PAD;
    return (
      <g style={{ pointerEvents: "none" }}>
        <text x={valueRight} y={y} dy="0.32em" textAnchor="end"
          style={{ fontSize: 10, fontWeight: 600, fill: "#666" }}>
          {fmt(value)}
        </text>
        <text x={nameLeft} y={y} dy="0.32em" textAnchor="start"
          style={{ fontSize: 11, fontWeight: 500, fill: "#333" }}>
          {name}
        </text>
      </g>
    );
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 260 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2,
      p: "12px 14px", mb: 1.5 }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// Horizontal single-metric bar chart. One row per campaign; the value
// and campaign name live in the left gutter (via YAxis tick), and the
// bar itself is never overlaid with text. `data` is pre-sorted desc so
// the longest bar is on top.
function CompareBarChartH({ data, dataKey, format = "int", color }) {
  const tickFmt = format === "pct" ? (v => `${v}%`)
                : format === "cur" ? (v => fmtCurrency(v))
                : (v => v);
  const tipFmt  = format === "pct" ? (v => [fmtPct(v), ""])
                : format === "cur" ? (v => [fmtCurrency(v), ""])
                : (v => [fmtInt(v), ""]);

  const nameW  = useMemo(() => nameColWidth(data), [data]);
  const gutter = gutterWidth(nameW);
  const rowsByName = useMemo(() => {
    const map = {};
    (data || []).forEach(r => { map[String(r.campaign)] = r; });
    return map;
  }, [data]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical"
        margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} tickFormatter={tickFmt} />
        <YAxis type="category" dataKey="campaign" width={gutter}
          axisLine={false} tickLine={false}
          tick={makeGutterTick(rowsByName, dataKey, format, nameW)} />
        <Tooltip
          contentStyle={{ fontSize: 12 }}
          formatter={tipFmt}
          labelFormatter={l => l}
        />
        <Bar dataKey={dataKey} radius={[0, 3, 3, 0]} isAnimationActive={false}>
          {data.map((entry, i) => (
            <Cell key={entry.campaign} fill={color || entry.color || SERIES_COLORS[i % SERIES_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Horizontal two-segment stacked bar. The first segment (green) is the
// "base" metric, the second (blue) stacks on top. The campaign name and
// the combined total live in the left gutter (via YAxis tick), so the
// bar geometry is never overlaid. Used for both the PTP %-vs-RPC chart
// and the Payments+Committed $ chart so they share one color concept.
//
//   baseKey   → green segment dataKey      (e.g. payPctVsRpc / paymentAmt)
//   topKey    → blue segment dataKey       (committedPctVsRpc / committedAmt)
//   totalKey  → combined value for gutter  (ptpPctVsRpc / payCommittedAmt)
//   format    → "pct" | "cur" | "int"      (axis ticks + value label)
//   baseName / topName → legend labels
function StackedChartH({ data, baseKey, topKey, totalKey, format, baseName, topName }) {
  const tickFmt = format === "pct" ? (v => `${v}%`)
                : format === "cur" ? (v => fmtCurrency(v))
                : (v => v);
  const tipFmt  = format === "pct" ? (v => [fmtPct(v), ""])
                : format === "cur" ? (v => [fmtCurrency(v), ""])
                : (v => [fmtInt(v), ""]);

  const nameW  = useMemo(() => nameColWidth(data), [data]);
  const gutter = gutterWidth(nameW);
  const rowsByName = useMemo(() => {
    const map = {};
    (data || []).forEach(r => { map[String(r.campaign)] = r; });
    return map;
  }, [data]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical"
        margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} tickFormatter={tickFmt} />
        <YAxis type="category" dataKey="campaign" width={gutter}
          axisLine={false} tickLine={false}
          tick={makeGutterTick(rowsByName, totalKey, format, nameW)} />
        <Tooltip
          contentStyle={{ fontSize: 12 }}
          formatter={(v, n) => [tipFmt(v)[0], n]}
          labelFormatter={l => l}
        />
        <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey={baseKey} name={baseName} stackId="stk"
          fill={GREEN} radius={[0, 0, 0, 0]} isAnimationActive={false} />
        <Bar dataKey={topKey} name={topName} stackId="stk"
          fill={BLUE} radius={[0, 3, 3, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Comparison table ────────────────────────────────────────────────
function CompareTable({ rows, cols, totals }) {
  const grid = `1.4fr repeat(${cols.length}, 1fr)`;
  const gridSx = { display: "grid", gridTemplateColumns: grid, px: 2 };

  const renderVal = (col, row) => {
    const v = safeNum(row[col.key]);
    const empty = v === 0;
    const color = empty ? "text.disabled" : (col.color || "text.primary");
    const text = col.type === "cur" ? fmtCurrency(v)
              : col.type === "pct" ? (empty ? "—" : fmtPct(v))
              : (empty ? "—" : v.toLocaleString());
    return <Box component="span" sx={{ color, fontWeight: empty ? 400 : 500 }}>{text}</Box>;
  };

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto", mb: 1.5 }}>
      {/* header */}
      <Box sx={{ ...gridSx, py: 1, background: "#f7f7f7", borderBottom: "0.5px solid",
        borderColor: "divider", fontSize: 11, fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        <Box sx={{ color: "text.secondary" }}>Campaign</Box>
        {cols.map(col => (
          <Box key={col.key} sx={{ textAlign: "right", color: "text.secondary" }}>{col.label}</Box>
        ))}
      </Box>
      {/* rows */}
      {rows.map((row, i) => (
        <Box key={row.campaign} sx={{ ...gridSx, py: "7px", fontSize: 12,
          borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "2px", flexShrink: 0,
              background: row.color }} />
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap", color: "text.primary" }}>{row.campaign}</Box>
          </Box>
          {cols.map(col => (
            <Box key={col.key} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              {renderVal(col, row)}
            </Box>
          ))}
        </Box>
      ))}
      {/* totals */}
      {totals && (
        <Box sx={{ ...gridSx, py: "8px", fontSize: 12, background: "#f0f5ff",
          borderTop: `1px solid ${BLUE}33` }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
          {cols.map(col => {
            const v = safeNum(totals[col.key]);
            const text = col.type === "cur" ? fmtCurrency(v)
                      : col.type === "pct" ? fmtPct(v)
                      : v.toLocaleString();
            return (
              <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end",
                color: col.color || BLUE, fontWeight: 700 }}>{text}</Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function CampaignComparisonDashboard({ channel }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const channelParam = (channel || "texts").toLowerCase();
  const isEmails = channelParam === "emails";

  const baseParams = useMemo(() => ({
    ...getDateRange(rangePreset),
    channel: channelParam,
  }), [rangePreset, channelParam]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { ...baseParams };

      const [texts, ptp, pay] = await Promise.all([
        getCompareTexts(params).catch(() => []),
        isEmails ? Promise.resolve([]) : getComparePtp(params).catch(() => []),
        isEmails ? Promise.resolve([]) : getComparePayments(params).catch(() => []),
      ]);

      const ptpBy = new Map((ptp || []).map(r => [r.campaign, r]));
      const payBy = new Map((pay || []).map(r => [r.campaign, r]));

      const names = [...new Set([
        ...(texts || []).map(r => r.campaign),
        ...ptpBy.keys(),
        ...payBy.keys(),
      ])].sort();

      const merged = names.map((name, idx) => {
        const t    = (texts || []).find(r => r.campaign === name) || {};
        const p    = ptpBy.get(name) || {};
        const pay2 = payBy.get(name) || {};

        const sent = safeNum(t.texts_sent);
        const resp = safeNum(t.texts_responded);

        // PtpDashboard convention: Payments and Committed PTP measured
        // against rpc_count, NOT sent. Total PTP % stays vs sent (API).
        const rpc       = safeNum(p.rpc_count);
        const payments  = safeNum(p.processed_ptp_count);
        const committed = safeNum(p.promise_count);
        const payPctVsRpc       = rpc > 0 ? (payments  / rpc) * 100 : 0;
        const committedPctVsRpc = rpc > 0 ? (committed / rpc) * 100 : 0;

        return {
          campaign: name,
          color:    SERIES_COLORS[idx % SERIES_COLORS.length],

          sent,
          responded:   resp,
          responsePct: sent > 0 ? (resp / sent) * 100 : 0,

          rpc,
          rpcPct:      safeNum(p.total_rpc_pct),
          payments,
          committed,
          payPctVsRpc,
          committedPctVsRpc,
          ptpPctVsRpc: payPctVsRpc + committedPctVsRpc,
          totalPtp:    safeNum(p.total_ptp_count),
          totalPtpPct: safeNum(p.total_ptp_pct),
          totalPtpAmt: safeNum(p.total_ptp_amount),
          // Payments + Committed $ only (excludes Uncommitted PTP $).
          paymentAmt:      safeNum(p.processed_ptp_amount),
          committedAmt:    safeNum(p.promise_amount),
          payCommittedAmt: safeNum(p.processed_ptp_amount) + safeNum(p.promise_amount),

          paymentCount:   safeNum(pay2.payment_count),
          paymentTotal:   safeNum(pay2.payment_total),
          promiseCount:   safeNum(pay2.promise_count),
          promiseTotal:   safeNum(pay2.promise_total),
          smartlinkCount: safeNum(pay2.smartlink_count),
          smartlinkTotal: safeNum(pay2.smartlink_total),
        };
      });

      const active = merged.filter(r =>
        r.sent > 0 || r.totalPtp > 0 || r.paymentTotal > 0 || r.promiseTotal > 0
      );
      setRows(active);
    } catch (e) {
      setError("Failed to load comparison data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [baseParams, isEmails]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const n = rows.length;

  // Tables default to sent desc for a readable baseline.
  const tableRows = useMemo(() => sortedBy(rows, "sent"), [rows]);

  // Per-chart sort orders (highest→lowest by the charted metric).
  const ptpStackRows   = useMemo(() => sortedBy(rows, "ptpPctVsRpc"),    [rows]);
  const ptpAmtRows     = useMemo(() => sortedBy(rows, "payCommittedAmt"), [rows]);
  const payAmtRows     = useMemo(() => sortedBy(rows, "paymentTotal"),   [rows]);
  const promiseAmtRows = useMemo(() => sortedBy(rows, "promiseTotal"),   [rows]);
  const slAmtRows      = useMemo(() => sortedBy(rows, "smartlinkTotal"), [rows]);
  const respPctRows    = useMemo(() => sortedBy(rows, "responsePct"),    [rows]);
  const rpcPctRows     = useMemo(() => sortedBy(rows, "rpcPct"),         [rows]);

  // ── Roll-up totals (table footers) ─────────────────────────────
  const totals = useMemo(() => {
    const t = tableRows.reduce((acc, r) => ({
      sent:           acc.sent           + r.sent,
      responded:      acc.responded      + r.responded,
      rpc:            acc.rpc            + r.rpc,
      payments:       acc.payments       + r.payments,
      committed:      acc.committed      + r.committed,
      totalPtp:       acc.totalPtp       + r.totalPtp,
      totalPtpAmt:    acc.totalPtpAmt    + r.totalPtpAmt,
      paymentCount:   acc.paymentCount   + r.paymentCount,
      paymentTotal:   acc.paymentTotal   + r.paymentTotal,
      promiseCount:   acc.promiseCount   + r.promiseCount,
      promiseTotal:   acc.promiseTotal   + r.promiseTotal,
      smartlinkCount: acc.smartlinkCount + r.smartlinkCount,
      smartlinkTotal: acc.smartlinkTotal + r.smartlinkTotal,
    }), {
      sent: 0, responded: 0, rpc: 0, payments: 0, committed: 0,
      totalPtp: 0, totalPtpAmt: 0,
      paymentCount: 0, paymentTotal: 0, promiseCount: 0, promiseTotal: 0,
      smartlinkCount: 0, smartlinkTotal: 0,
    });
    return {
      ...t,
      responsePct:       t.sent > 0 ? (t.responded / t.sent) * 100 : 0,
      rpcPct:            t.sent > 0 ? (t.rpc       / t.sent) * 100 : 0,
      totalPtpPct:       t.sent > 0 ? (t.totalPtp  / t.sent) * 100 : 0,
      payPctVsRpc:       t.rpc  > 0 ? (t.payments  / t.rpc)  * 100 : 0,
      committedPctVsRpc: t.rpc  > 0 ? (t.committed / t.rpc)  * 100 : 0,
    };
  }, [tableRows]);

  // ── Table column defs ───────────────────────────────────────────
  const ptpCols = [
    { key: "sent",              label: "Sent",          type: "int" },
    { key: "rpc",               label: "RPC",           type: "int", color: GREEN },
    { key: "rpcPct",            label: "RPC %",         type: "pct", color: GREEN },
    { key: "payments",          label: "Payments",      type: "int", color: GREEN },
    { key: "payPctVsRpc",       label: "Pay % / RPC",   type: "pct", color: GREEN },
    { key: "committed",         label: "PTP",           type: "int", color: BLUE  },
    { key: "committedPctVsRpc", label: "PTP % / RPC",   type: "pct", color: BLUE  },
    { key: "totalPtp",          label: "Total PTP",     type: "int", color: AMBER },
    { key: "totalPtpPct",       label: "PTP % / sent",  type: "pct", color: AMBER },
    { key: "totalPtpAmt",       label: "PTP $",         type: "cur", color: AMBER },
  ];

  const payCols = [
    { key: "paymentCount",   label: "Payments",     type: "int", color: BLUE },
    { key: "paymentTotal",   label: "Payments $",   type: "cur", color: BLUE },
    { key: "promiseCount",   label: "PTP",          type: "int", color: GREEN },
    { key: "promiseTotal",   label: "PTP $",        type: "cur", color: GREEN },
    { key: "smartlinkCount", label: "Smart links",  type: "int", color: PURPLE },
    { key: "smartlinkTotal", label: "Smart link $", type: "cur", color: PURPLE },
  ];

  const trendsCols = [
    { key: "sent",        label: `${isEmails ? "Emails" : "Texts"} sent`, type: "int" },
    { key: "responded",   label: "Responses",  type: "int", color: PINK },
    { key: "responsePct", label: "Response %", type: "pct", color: AMBER },
  ];

  // Two-column grid for charts so we don't burn a full row each. With
  // many campaigns the charts are tall, but pairing them halves the
  // page length vs the dedicated-row layout.
  const gridTwo = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers (no campaign dropdown — we compare them all) ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range" options={RANGE_OPTIONS}
          value={rangePreset} onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
        <Box sx={{ ml: "auto", alignSelf: "flex-end", fontSize: 11, color: "text.secondary" }}>
          {n} campaign{n === 1 ? "" : "s"} with activity
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Comparing campaigns…
        </Box>
      )}

      {!loading && !error && n === 0 && (
        <Box sx={{ mt: 2, p: 3, borderRadius: 2, border: "0.5px solid", borderColor: "divider",
          color: "text.secondary", fontSize: 13 }}>
          No campaign activity in the selected period.
        </Box>
      )}

      {!loading && n > 0 && (
        <>
          {/* ── PTP / PAYMENTS — texts only ── */}
          {!isEmails && (
            <>
              {/* ── PTP CONVERSION (top) ── */}
              <SectionTitle>PTP — conversion rates (vs RPC)</SectionTitle>
              <Box sx={gridTwo}>
                <ChartCard title="Payments + PTP % (stacked, vs RPC)"
                  height={chartHeight(n, STACK_CHROME)}>
                  <StackedChartH data={ptpStackRows}
                    baseKey="payPctVsRpc" topKey="committedPctVsRpc" totalKey="ptpPctVsRpc"
                    format="pct"
                    baseName="Payments % (vs RPC)" topName="PTP % (vs RPC)" />
                </ChartCard>
                <ChartCard title="Payments + PTP $ (stacked)"
                  height={chartHeight(n, STACK_CHROME)}>
                  <StackedChartH data={ptpAmtRows}
                    baseKey="paymentAmt" topKey="committedAmt" totalKey="payCommittedAmt"
                    format="cur"
                    baseName="Payments $" topName="PTP $" />
                </ChartCard>
              </Box>

              <SectionTitle>PTP — detail</SectionTitle>
              <CompareTable rows={tableRows} cols={ptpCols} totals={totals} />

              {/* ── PAYMENT $ AMOUNTS ── */}
              <SectionTitle>Payments — dollar amounts</SectionTitle>
              <Box sx={gridTwo}>
                <ChartCard title="Payments $" height={chartHeight(n)}>
                  <CompareBarChartH data={payAmtRows} dataKey="paymentTotal" format="cur" color={BLUE} />
                </ChartCard>
                <ChartCard title="PTP $" height={chartHeight(n)}>
                  <CompareBarChartH data={promiseAmtRows} dataKey="promiseTotal" format="cur" color={GREEN} />
                </ChartCard>
              </Box>
              <ChartCard title="Smart link $" height={chartHeight(n)}>
                <CompareBarChartH data={slAmtRows} dataKey="smartlinkTotal" format="cur" color={PURPLE} />
              </ChartCard>

              <SectionTitle>Payments — detail</SectionTitle>
              <CompareTable rows={tableRows} cols={payCols} totals={totals} />
            </>
          )}

          {/* ── TEXTS / EMAILS — engagement + volume (bottom) ── */}
          <SectionTitle>{isEmails ? "Emails" : "Texts"} — engagement rates</SectionTitle>
          <Box sx={gridTwo}>
            <ChartCard title="Response %" height={chartHeight(n)}>
              <CompareBarChartH data={respPctRows} dataKey="responsePct" format="pct" color={AMBER} />
            </ChartCard>
            {!isEmails && (
              <ChartCard title="RPC %" height={chartHeight(n)}>
                <CompareBarChartH data={rpcPctRows} dataKey="rpcPct" format="pct" color={GREEN} />
              </ChartCard>
            )}
          </Box>

          <SectionTitle>{isEmails ? "Emails" : "Texts"} — volume</SectionTitle>
          <CompareTable rows={tableRows} cols={trendsCols} totals={totals} />

          {isEmails && (
            <Box sx={{ mt: 2, p: 3, borderRadius: 2, border: "0.5px solid", borderColor: "divider",
              color: "text.secondary", fontSize: 13 }}>
              PTP and payment comparisons are only available for the Texts channel.
            </Box>
          )}
        </>
      )}
    </Box>
  );
}